import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiEndpoints from "../apiconfig";
// Import step components
import PersonalDetails from "./profilesetup/PersonalDetails";
import FamilyInformation from "./profilesetup/FamilyInformation";
import PartnerPreferences from "./profilesetup/PartnerPreferences";
import UploadPhotos from "./profilesetup/UploadPhotos";
import UploadHoroscope from "./profilesetup/UploadHoroscope";
import UploadIdProof from "./profilesetup/UploadIdProof";
import BasicDetails from "./profilesetup/BasicDetails";
import { useLocation } from "react-router-dom";

export default function EditProfile() {
  const [step, setStep] = useState(1);
  const totalSteps = 6;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [showErrors, setShowErrors] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const pageNames = {
    1: "Personal Details",
    2: "Family Information",
    3: "Partner Preferences",
    4: "Upload Photos",
    5: "Upload Horoscope",
    6: "Upload ID Proof",
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Required fields for validation
  const requiredFieldsByStep = {
    1: [
      "name",
      "email",
      "age",
      "dob",
      "birth_place",
      "birth_time",
      "gender",
      "education",
      "blood_group",
      "address",
      "country",
      "state",
      "city",
      "marital_status",
      "profession",
      "annual_income",
      "religion",
      "caste",
      "zodiac_sign",
      "zodiac_star",
    ],
    2: [
      "family_type",
      "father_name",
      "mother_name",
      "father_occupation",
      "mother_occupation",
      "family_status",
    ],
    3: [
      "pref_religion",
      "pref_country",
      "pref_state",
      "pref_city",
      "pref_marital_status",
      "pref_blood_group",
      "pref_caste",
      "pref_zodiac_sign",
      "pref_zodiac_star",
      "pref_education",
      "pref_profession",
      "pref_annual_income",
    ],
    4: ["profile_photo", "profile_photo1", "profile_photo2", "profile_photo3"],
    5: ["horoscope"],
    6: ["ID_proof"],
  };

  const [form, setForm] = useState({
    // Personal
    name: "",
    email: "",
    phone: "",
    age: "",
    dob: "",
    birth_place: "",
    birth_time: "",
    gender: "",
    address: "",
    about_me: "",

    education: "",
    blood_group: "",
    country: "",
    country_id: null,
    state: "",
    state_id: null,
    city: "",
    city_id: null,
    marital_status: "",
    profession: "",
    annual_income: "",
    religion: "",
    caste: "",
    zodiac_sign: "",
    zodiac_star: "",

    // Family
    family_type: "",
    father_name: "",
    mother_name: "",
    father_occupation: "",
    mother_occupation: "",
    no_of_brothers: "",
    no_of_sisters: "",
    family_status: "",
    about_family: "",

    // Partner Prefs
    pref_religion: "",
    pref_country: "",
    pref_country_id: null,
    pref_state: "",
    pref_state_id: null,
    pref_city: "",
    pref_city_id: null,
    pref_marital_status: "",
    pref_blood_group: "",
    pref_caste: "",
    pref_zodiac_sign: "",
    pref_zodiac_star: "",
    pref_education: "",
    pref_profession: "",
    pref_annual_income: "",

    // Uploads
    profile_photo: null,
    profile_photo1: null,
    profile_photo2: null,
    profile_photo3: null,
    horoscope: null,
    ID_proof: null,
  });

  const location = useLocation();
  const user = location.state?.user;

  useEffect(() => {
    if (!user) return;

    const fetchFullDetails = async () => {
      try {
        const response = await fetch(
          `${apiEndpoints.register}?action=fetch&id=${user.id}`,
        );
        const resData = await response.json();
        if (resData.status === "success" && resData.data) {
          const u = resData.data;
          setForm({
            // Personal details
            name: u.name || "",
            email: u.email || "",
            phone: u.phone || "",
            age: u.age || "",
            dob: u.dob || "",
            birth_place: u.birth_place || "",
            birth_time: u.birth_time || "",
            gender: u.gender || "",
            education: u.education || "",
            blood_group: u.blood_group || "",
            address: u.address || "",
            country: u.country || "",
            country_id: u.country_id || null,
            state: u.state || "",
            state_id: u.state_id || null,
            city: u.city || "",
            city_id: u.city_id || null,
            marital_status: u.marital_status || "",
            profession: u.profession || "",
            annual_income: u.annual_income || "",
            religion: u.religion || "",
            caste: u.caste || "",
            zodiac_sign: u.zodiac_sign || "",
            zodiac_star: u.zodiac_star || "",
            about_me: u.about_me || "",

            // Family information
            family_type: u.family_type || "",
            father_name: u.father_name || "",
            mother_name: u.mother_name || "",
            father_occupation: u.father_occupation || "",
            mother_occupation: u.mother_occupation || "",
            no_of_brothers: u.no_of_brothers || "",
            no_of_sisters: u.no_of_sisters || "",
            family_status: u.family_status || "",
            about_family: u.about_family || "",

            // Partner preferences
            pref_religion: u.pref_religion || "",
            pref_education: u.pref_education || "",
            pref_country: u.pref_country || "",
            pref_country_id: u.pref_country_id || null,
            pref_state: u.pref_state || "",
            pref_state_id: u.pref_state_id || null,
            pref_city: u.pref_city || "",
            pref_city_id: u.pref_city_id || null,
            pref_marital_status: u.pref_marital_status || "",
            pref_blood_group: u.pref_blood_group || "",
            pref_profession: u.pref_profession || "",
            pref_caste: u.pref_caste || "",
            pref_zodiac_sign: u.pref_zodiac_sign || "",
            pref_zodiac_star: u.pref_zodiac_star || "",
            pref_annual_income: u.pref_annual_income || "",

            // Uploads
            profile_photo: u.profile_photo || null,
            profile_photo1: null,
            profile_photo2: null,
            profile_photo3: null,
            horoscope: u.horoscope || null,
            ID_proof: u.ID_proof || null,
          });
        }
      } catch (err) {
        console.error("Failed to fetch full user details", err);
      }
    };

    fetchFullDetails();
  }, [user, apiEndpoints.register]);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  console.log("User from API:", user);
  console.log("Setting education id:", user.education);

  const handleNext = () => {
    let stepFields = requiredFieldsByStep[step] || [];

    // Skip validation for Caste, Zodiac Sign, and Star if religion is Muslim
    if (step === 1 && form.religion?.toLowerCase().includes("muslim")) {
      stepFields = stepFields.filter(
        (field) => !["caste", "zodiac_sign", "zodiac_star"].includes(field),
      );
    }

    // Skip validation for Partner Preferences (Step 3) if religion is Muslim
    if (step === 3 && form.pref_religion?.toLowerCase().includes("muslim")) {
      stepFields = stepFields.filter(
        (field) =>
          !["pref_caste", "pref_zodiac_sign", "pref_zodiac_star"].includes(
            field,
          ),
      );
    }

    const isValid = stepFields.every((field) => {
      if (step === 4) {
        return (
          form.profile_photo ||
          form.profile_photo1 ||
          form.profile_photo2 ||
          form.profile_photo3
        );
      }
      const val = form[field];
      if (!val) console.log("Validation failed for field:", field, val);
      return val;
    });

    if (!isValid) {
      setShowErrors(true);
      return;
    }

    setShowErrors(false);
    showSnackbar(`${pageNames[step]} updated successfully!`, "success");
    setStep((s) => Math.min(totalSteps, s + 1));
  };

  // File uploads
  const handleMultiImageUpload = (e) => {
    const files = Array.from(e.target.files);

    const existingPhotos = [
      form.profile_photo,
      form.profile_photo1,
      form.profile_photo2,
      form.profile_photo3,
    ].filter(Boolean).length;

    if (existingPhotos + files.length > 4) {
      showSnackbar("You can only upload a maximum of 4 photos", "error");
      return;
    }
    const keys = [
      "profile_photo",
      "profile_photo1",
      "profile_photo2",
      "profile_photo3",
    ];

    setForm((prev) => {
      const updated = { ...prev };
      let i = 0;
      keys.forEach((key) => {
        if (!updated[key] && files[i]) {
          updated[key] = {
            file: files[i],
            name: files[i].name,
            url: URL.createObjectURL(files[i]), // for preview
          };
          i++;
        }
      });
      return updated;
    });
  };

  const handleSingleImageUpload = (e, key) => {
    const file = (e.target.files || [])[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      alert("Only JPG/PNG files are allowed.");
      return;
    }

    setField(key, { file, name: file.name, url: URL.createObjectURL(file) });
  };

  const removePhotoAt = (idx) => {
    const keys = [
      "profile_photo",
      "profile_photo1",
      "profile_photo2",
      "profile_photo3",
    ];
    setForm((p) => {
      const updated = { ...p };
      if (keys[idx]) updated[keys[idx]] = null;
      return updated;
    });
  };

  function dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  // Final submit
  const onSubmit = async () => {
    if (
      !form.profile_photo &&
      !form.profile_photo1 &&
      !form.profile_photo2 &&
      !form.profile_photo3
    ) {
      showSnackbar("At least 1 photo is required", "error");
      return;
    }
    if (!form.horoscope) {
      showSnackbar("Horoscope image is required", "error");
      return;
    }
    if (!form.ID_proof) {
      showSnackbar("ID Proof is required", "error");
      return;
    }

    const formData = new FormData();
    formData.append("action", "update");
    formData.append("id", user.id);

    const fieldsToSend = [
      "name",
      "email",
      "phone",
      "age",
      "dob",
      "birth_place",
      "birth_time",
      "gender",
      "address",
      "about_me",
      "father_name",
      "mother_name",
      "father_occupation",
      "mother_occupation",
      "no_of_brothers",
      "no_of_sisters",
      "family_status",
      "about_family",
      "education",
      "blood_group",
      "country",
      "state",
      "city",
      "marital_status",
      "profession",
      "annual_income",
      "religion",
      "caste",
      "zodiac_sign",
      "zodiac_star",
      "family_type",
      "pref_religion",
      "pref_country",
      "pref_state",
      "pref_city",
      "pref_marital_status",
      "pref_blood_group",
      "pref_caste",
      "pref_zodiac_sign",
      "pref_zodiac_star",
      "pref_education",
      "pref_profession",
      "pref_annual_income",
      "profile_photo",
      "profile_photo1",
      "profile_photo2",
      "profile_photo3",
      "horoscope",
      "ID_proof",
    ];

    fieldsToSend.forEach((field) => {
      if (form[field] !== undefined && form[field] !== null) {
        if (form[field]?.file) {
          formData.append(field, form[field].file);
        } else {
          formData.append(field, form[field]);
        }
      }
    });

    try {
      const res = await fetch(apiEndpoints.userDetails, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        showSnackbar(data.message || "Profile updated successfully", "success");
        setTimeout(() => {
          navigate("/dashboard/users", { replace: true });
        }, 1500);
      } else {
        showSnackbar(data.message || "Update failed", "error");
      }
    } catch (err) {
      console.error("Update error:", err);
      showSnackbar("Something went wrong", "error");
    }
  };

  // Render steps
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <PersonalDetails
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 2:
        return (
          <FamilyInformation
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 3:
        return (
          <PartnerPreferences
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 4:
        return (
          <UploadPhotos
            form={form}
            handleMultiImageUpload={handleMultiImageUpload}
            removePhotoAt={removePhotoAt}
            setField={setField}
            showErrors={showErrors}
          />
        );
      case 5:
        return (
          <UploadHoroscope
            form={form}
            handleSingleImageUpload={handleSingleImageUpload}
            setField={setField}
            showErrors={showErrors}
          />
        );
      case 6:
        return (
          <UploadIdProof
            form={form}
            handleSingleImageUpload={handleSingleImageUpload}
            setField={setField}
            showErrors={showErrors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: "90%", mx: "auto", p: { xs: 2, md: 3 } }}>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => navigate("/dashboard/users")}
              sx={{
                bgcolor: "rgba(0,0,0,0.05)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
              }}
            >
              <ArrowLeft size={20} color="black" />
            </IconButton>
            <Stack spacing={0.5}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Edit User Profile
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Step {step} of {totalSteps}
              </Typography>
            </Stack>
          </Stack>
        }
        sx={{ pb: 0 }}
      />

      <CardContent
        sx={{
          overflowY: "auto",
          maxHeight: { xs: "calc(85vh - 160px)", md: "calc(85vh - 170px)" },
          pt: 2,
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {renderStep()}
      </CardContent>

      <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ChevronLeft />}
          disabled={step === 1}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
        >
          Previous
        </Button>

        {step === totalSteps ? (
          <Button
            variant="contained"
            onClick={onSubmit}
            sx={{
              backgroundColor: "#ffe599",
              color: "black",
              borderRadius: 2,
              "&:hover": { backgroundColor: "#ffe599" },
            }}
          >
            Save Profile
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ChevronRight />}
            onClick={handleNext}
            sx={{
              backgroundColor: "#ffe599",
              color: "black",
              "&:hover": { backgroundColor: "#ffe599" },
            }}
          >
            Next
          </Button>
        )}
      </CardActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbar-root": {
            top: "80px",
            right: "24px",
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          //variant="filled"
          sx={{
            width: "100%",
            fontFamily: "'Montserrat', sans-serif",
            alignItems: "center",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
